import { Message } from "@fullscript/aviary-web";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useInviteContext } from "@unauthenticated/shared/hooks/useInviteContext/useInviteContext";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface Props {
  storeName: string;
}

/**
 * Combined social-context + consent banner for the invite-link
 * signup flow. Shown above the form on the welcome and signup pages
 * whenever the user arrived via an invite link. Renders `null`
 * otherwise so it's safe to drop into shared scenes.
 *
 * Copy explicitly enumerates what the inviter will (and won't) see
 * about the invitee — the user authorizes that disclosure by
 * proceeding with signup (Option A from TTN-2421, no checkbox).
 *
 * Fires `patient.invite_link_consent_shown` once per mount (guarded
 * by a ref to survive re-renders). Each page in the flow mounts its
 * own instance so we get one event per page view — intentional for
 * the audit trail. The matching `_acknowledged` event is fired in
 * `PatientStoreSignUpForm`'s `onCompleted` callback, gated on a
 * successful signup (no errors) so retries/failures don't emit.
 */
const InvitedByBanner = ({ storeName }: Props) => {
  const { t } = useTranslation();
  const inviteContext = useInviteContext();
  const logAnalyticsEvent = useAnalyticsLogger();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (!inviteContext || hasLoggedRef.current) return;

    hasLoggedRef.current = true;
    logAnalyticsEvent({
      eventName: AnalyticsEvent.PATIENT_INVITE_LINK_CONSENT_SHOWN,
      payload: {},
    });
  }, [inviteContext, logAnalyticsEvent]);

  if (!inviteContext) return null;

  return (
    <Message
      intention="info"
      label={t(l.patientRxAuth.invitedByBanner.text, {
        invitingPatientName: inviteContext.invitingPatientName,
        storeName,
      })}
    />
  );
};

export { InvitedByBanner };
